import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from '@reach/router';
import qs from 'query-string';
import { E164Number } from 'libphonenumber-js';
import { useLocalStorageState } from '@clover-util/use-storage-state';

import { useConfirmEmailAuthenticationRequest } from 'hooks/useCustomerAuthApi/useConfirmEmailAuthenticationRequest';
import { useConfirmPhoneAuthenticationRequest } from 'hooks/useCustomerAuthApi/useConfirmPhoneAuthenticationRequest';
import { useRefreshTokenRequest } from 'hooks/useCustomerAuthApi/useRefreshTokenRequest';
import { useEmailAuthenticationRequest } from 'hooks/useCustomerAuthApi/useEmailAuthenticationRequest';
import { usePhoneAuthenticationRequest } from 'hooks/useCustomerAuthApi/usePhoneAuthenticationRequest';
import { useMerchant } from '../merchant';
import { createHookContext } from '@clover-util/react-context';
import { useCustomerStorageExpiry } from 'hooks/useStorageExpiry';
import { useSmartNavigate } from 'hooks/useSmartNavigate';
import { useGetCustomer } from 'hooks/useApi/useGetCustomer';

import { GlobalCustomer } from 'olo-service';
import { ddRemoveUser, ddSetUser } from 'util/Analytics';
import { SessionStorage } from 'util/Storage';

export const SESSION_KEY = 'colo-customer-session';
export const REFRESH_KEY = 'colo-customer-refresh';
export const STAY_LOGGED_IN = 'colo-customer-stay-logged-in';
const SESSION_KEY_EXPIRY = 'colo-customer-session_updated_on';
const REFRESH_KEY_EXPIRY = 'colo-customer-refresh_updated_on';

export const { useCustomer, CustomerProvider } = createHookContext('Customer', () => {
  // States
  const [sessionToken, setSessionToken] = useLocalStorageState<string>(SESSION_KEY);
  const [refreshToken, setRefreshToken] = useLocalStorageState<string>(REFRESH_KEY);
  const [isMagicLinkExpired, setIsMagicLinkExpired] = useState(false);
  const [chosenMarketingAllowed, setChosenMarketingAllowed] = useState(false);
  const [stayLoggedIn, setStayLoggedIn] = useState(true);
  const [chosenStayLoggedIn, setChosenStayLoggedIn] = useLocalStorageState<boolean>(STAY_LOGGED_IN);
  const [logOutButtonClicked, setLogOutButtonClicked] = useState(false);

  // Hooks
  const navigate = useSmartNavigate();
  const { merchant } = useMerchant();
  const location = useLocation();
  const { loading: isLoading, globalCustomer, error } = useGetCustomer(merchant?.merchantUuid, sessionToken);
  const [isExpired, updateExpiry] = useCustomerStorageExpiry(SESSION_KEY_EXPIRY, { hours: 1 });
  const [isRefreshTokenExpired, updateRefreshTokenExpiry] = useCustomerStorageExpiry(REFRESH_KEY_EXPIRY,{ years: 1 });

  // Api Requests
  const [, phoneAuthRequest] = usePhoneAuthenticationRequest();
  const [, confirmPhoneAuthRequest] = useConfirmPhoneAuthenticationRequest();
  const [, emailAuthRequest] = useEmailAuthenticationRequest();
  const [, confirmEmailAuth] = useConfirmEmailAuthenticationRequest();
  const [, refreshTokenRequest] = useRefreshTokenRequest();

  // Clear session token
  const clearToken = useCallback(()=> {
    setSessionToken(undefined);
  }, [setSessionToken]);

  // Clear session token
  const clearRefreshToken = useCallback(()=> {
    setRefreshToken(undefined);
    setChosenStayLoggedIn(false);
  }, [setRefreshToken, setChosenStayLoggedIn]);

  /*
   API CALLS
  */
  const sendPhoneCode = useCallback(async (phoneNumber: E164Number, reCaptchaToken?: RecaptchaToken) => {
    await phoneAuthRequest(phoneNumber, reCaptchaToken);
  }, [phoneAuthRequest]);

  const sendEmailAuth = useCallback(async (emailAddress: string, redirectPath?: string, reCaptchaToken?: RecaptchaToken) => {
    // Remove ~ from path
    const path = redirectPath ? `/${redirectPath.substring(1)}` : '';
    await emailAuthRequest(emailAddress, `${merchant?.slug}${path}`, reCaptchaToken);
  }, [emailAuthRequest, merchant?.slug]);

  const sendRefreshToken = useCallback(async (refreshToken?: RefreshToken) => {
    const response = await refreshTokenRequest(refreshToken);
    setSessionToken(response?.sessionToken);
    updateExpiry();
  }, [refreshTokenRequest, updateExpiry, setSessionToken]);

  const confirmPhoneCode = useCallback(async (phoneNumber: E164Number, code: string) => {
    const response = await confirmPhoneAuthRequest(code, phoneNumber);

    setSessionToken(response?.sessionToken);
    updateExpiry();
    setRefreshToken(response?.refreshToken);
    updateRefreshTokenExpiry();
  }, [confirmPhoneAuthRequest, setSessionToken, setRefreshToken, updateExpiry, updateRefreshTokenExpiry]);

  const confirmEmailAuthToken = useCallback(async (authorizationToken: string) => {
    try {
      const response = await confirmEmailAuth(authorizationToken);
      setSessionToken(response?.sessionToken);
      updateExpiry();
      setRefreshToken(response?.refreshToken);
      updateRefreshTokenExpiry();
    } catch (e) {
      setIsMagicLinkExpired(true);
      navigate('~menu');
    }

  }, [confirmEmailAuth, navigate, setSessionToken, setRefreshToken, updateExpiry, updateRefreshTokenExpiry]);

  /*
    Confirm Email Token onload
  */

  const params = qs.parse(location.search);
  const token = params['token'] && typeof params['token'] == 'string' ? params['token'] : undefined;
  const isOnloadPopupTouched = SessionStorage.tryGetItem('colo_onloadpop_istouched');
  const setOnLoadPopupIsTouched = token && !isOnloadPopupTouched;

  useEffect(() => {
    if (setOnLoadPopupIsTouched) SessionStorage.trySetItem('colo_onloadpop_istouched', 'true');
  }, [setOnLoadPopupIsTouched]);

  useEffect(() => {
    if (token && !sessionToken && !logOutButtonClicked) {
      confirmEmailAuthToken(token);
    }
  }, [confirmEmailAuthToken, sessionToken, token, logOutButtonClicked]);

  // Clear session token when expired or on 401
  useEffect(() => {
    if (!isRefreshTokenExpired && isExpired && chosenStayLoggedIn && refreshToken) sendRefreshToken(refreshToken);
    if (isExpired || error?.response?.status === 401) clearToken();
    if (isRefreshTokenExpired) clearRefreshToken();
  }, [isRefreshTokenExpired, isExpired, error, clearToken, clearRefreshToken, chosenStayLoggedIn, refreshToken, sendRefreshToken]);
  /*
    Customer Data
  */
  const customer = useMemo<GlobalCustomer | undefined>(() => {
    if (!globalCustomer || !sessionToken) {
      ddRemoveUser();

      return;
    }

    ddSetUser(globalCustomer.globalCustomerUuid);

    return globalCustomer;
  }, [globalCustomer, sessionToken]);

  return {
    isLoggedIn: useMemo(() => !!sessionToken, [sessionToken]),
    sessionToken,
    sendPhoneCode,
    confirmPhoneCode,
    sendEmailAuth,
    confirmEmailAuthToken,
    sendRefreshToken,
    refreshToken,
    clearToken,
    isMagicLinkExpired,
    stayLoggedIn,
    setStayLoggedIn,
    chosenMarketingAllowed,
    setChosenMarketingAllowed,
    chosenStayLoggedIn,
    setChosenStayLoggedIn,
    customer,
    token,
    setLogOutButtonClicked,
    isGetCustomerLoading: useMemo(() => (!customer && !error) || isLoading, [customer, error, isLoading])
  };

});
